/**
 * aw26-cart-progress.js
 * Live in-place update for every cart progress bar rendered by
 * snippets/free-shipping-bar.liquid. The bar can appear on three surfaces at
 * once: the cart drawer, the product page (compact variant) and the cart page.
 * A single delegated cart:update listener keeps them all in sync.
 *
 * Why in-place rather than a full re-render: mutating --cart-fill on the
 * existing node lets the CSS width transition animate from the old value to the
 * new one. The subsequent server-rendered HTML carries the same final value, so
 * any morph is a no-op and the transition runs to completion.
 *
 * Loaded once (guarded) from cart-drawer.liquid, which is present on every page.
 */
(function () {
  if (window.__cartProgressLiveBound) return;
  window.__cartProgressLiveBound = true;

  function formatEuro(cents) {
    var amt = cents / 100;
    if (amt === Math.floor(amt)) return '€' + Math.floor(amt);
    return '€' + amt.toFixed(2).replace('.', ',');
  }

  function cartTotalRequiringShipping(cart) {
    var total = 0;
    (cart.items || []).forEach(function (item) {
      var isGift = item.properties && item.properties._free_gift === 'true';
      if (item.requires_shipping && !isGift) {
        total += item.final_line_price;
      }
    });
    return total;
  }

  // Rebuilds the status line above the bar to mirror the server-side Liquid
  // logic exactly, using the translated copy the server exposed on the bar's
  // data-* attributes. Copy stays in the locale files; only the branch logic
  // is shared here so the header can never contradict the fill after a live
  // cart change. Text is written via textContent (no HTML injection).
  function renderStatus(statusEl, bar, total, gift, ship, reachedGift, reachedShip) {
    var d = bar.dataset;
    var hasGift = gift > 0;
    var hasShip = ship > 0;
    var equal = hasGift && hasShip && gift === ship;

    var p = document.createElement('p');
    p.className = 'cart-progress__message';

    function asSuccess(text) {
      p.classList.add('cart-progress__message--success');
      p.textContent = text || '';
    }
    function asProgress(remCents, text) {
      var strong = document.createElement('strong');
      strong.setAttribute('data-cart-progress-remaining', '');
      strong.textContent = formatEuro(remCents);
      p.appendChild(strong);
      p.appendChild(document.createTextNode(' ' + (text || '')));
    }

    if (reachedGift && reachedShip) {
      asSuccess(d.copyBoth);
    } else if (reachedShip && !hasGift) {
      asSuccess(d.copyShippingUnlocked);
    } else if (reachedGift && !hasShip) {
      asSuccess(d.copyGiftUnlocked);
    } else if (equal) {
      asProgress(Math.max(0, ship - total), d.copyUntilBoth);
    } else {
      // Pick the next unreached milestone, lowest threshold first - mirrors the
      // Liquid next_milestone assignment.
      var next = '';
      if (hasGift && !reachedGift) {
        if (hasShip && !reachedShip) next = gift <= ship ? 'gift' : 'shipping';
        else next = 'gift';
      } else if (hasShip && !reachedShip) {
        next = 'shipping';
      }
      if (next === 'gift') asProgress(Math.max(0, gift - total), d.copyUntilGift);
      else if (next === 'shipping') asProgress(Math.max(0, ship - total), d.copyUntilShipping);
      else {
        // Nothing to report (no thresholds unreached) - clear the line.
        while (statusEl.firstChild) statusEl.removeChild(statusEl.firstChild);
        return;
      }
    }

    while (statusEl.firstChild) statusEl.removeChild(statusEl.firstChild);
    statusEl.appendChild(p);
  }

  function updateBar(bar, total) {
    var gift = parseInt(bar.dataset.giftThreshold, 10) || 0;
    var ship = parseInt(bar.dataset.shippingThreshold, 10) || 0;
    var max = Math.max(gift, ship);
    if (max <= 0) return;

    var pct = (total * 100) / max;
    if (pct < 0) pct = 0;
    if (pct > 100) pct = 100;

    // Single source of truth on the stable parent; the fill width reads this
    // var and the CSS transition animates the change.
    bar.style.setProperty('--cart-fill', pct + '%');

    // Reached-state is derived from the SAME total that drives the fill, so the
    // header copy, the milestone checkmarks and the fill can never disagree
    // after a live cart change (the previous version updated only the fill,
    // leaving a stale "unlocked" header + checkmarks below the threshold).
    var reachedGift = gift > 0 && total >= gift;
    var reachedShip = ship > 0 && total >= ship;

    // Header status copy (skipped on stale markup that predates the data-copy-*
    // attributes so we never blank the line).
    var statusEl = bar.querySelector('.cart-progress__status');
    if (statusEl && bar.dataset.copyUntilGift != null) {
      renderStatus(statusEl, bar, total, gift, ship, reachedGift, reachedShip);
    }

    // Milestone rows drive their own checkmark visibility via .is-reached (CSS).
    // 'gift' rows track the gift threshold; 'shipping'/'both' rows track shipping.
    bar.querySelectorAll('[data-milestone]').forEach(function (row) {
      var reached = row.dataset.milestone === 'gift' ? reachedGift : reachedShip;
      row.classList.toggle('is-reached', reached);
    });

    // Static markers on the track.
    var checkpoint = bar.querySelector('.cart-progress__checkpoint--gift');
    if (checkpoint) checkpoint.classList.toggle('is-reached', reachedGift);

    var endIcon = bar.querySelector('.cart-progress__end-icon');
    if (endIcon) {
      var endReached = endIcon.classList.contains('cart-progress__end-icon--shipping')
        ? reachedShip
        : reachedGift;
      endIcon.classList.toggle('is-reached', endReached);
    }

    var fill = bar.querySelector('[data-cart-progress-fill]');
    if (fill) fill.classList.toggle('cart-progress__fill--complete', reachedGift && reachedShip);

    // Force the gift expand state to mirror the cart total (drawer/cart bars
    // only; the compact product-page bar has no <details>).
    var details = bar.querySelector('.cart-progress__expand');
    if (details && gift > 0) {
      var shouldBeOpen = total >= gift;
      if (details.open !== shouldBeOpen) details.open = shouldBeOpen;
    }
  }

  function updateAll(cart) {
    if (!cart || !cart.items) return;
    var bars = document.querySelectorAll('[data-cart-progress]');
    if (!bars.length) return;
    var total = cartTotalRequiringShipping(cart);
    bars.forEach(function (bar) {
      updateBar(bar, total);
    });
  }

  // Bumped on every cart:update so a slow fallback fetch cannot overwrite a newer state.
  var cartUpdateGeneration = 0;

  document.addEventListener('cart:update', function (e) {
    var generation = ++cartUpdateGeneration;
    var cart = e.detail && e.detail.resource;
    // Only trust the resource when it is a full cart object. /cart/add.js
    // responses (upsell, wishlist) carry only the added lines and have no
    // item_count, so those fall through to a fresh /cart.js fetch.
    if (cart && cart.items && typeof cart.item_count === 'number') {
      updateAll(cart);
    } else {
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (c) {
          if (generation === cartUpdateGeneration) updateAll(c);
        })
        .catch(function () {});
    }
  });
})();
